import AppCard from '@nxsys/components/AppCard';
import { Icons } from '@nxsys/types/icons';
import { useState } from 'react';
import CreateEmployeeModal from '../../modules/employees/create';
import FPSModal from './components/FPSModal';
import EPSModal from './components/EPSModal';
import CIS300Modal from './components/CIS300Modal';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import PSRModal, { PSRModalTypeEnum } from './components/PSRModal';
import { hasAllDepartmentCompany } from '@nxsys/utils/department-scope';

const EmployerIWantTo = () => {
  const { payrollCountry, user, policies } = useSelector(
    (state: AppState) => state.appStore,
  );
  // Submit FPS/EPS/PSR/Pension and Email Payslips are company-wide payroll
  // actions, never filtered to a subset of departments — hide them for a user
  // with no all-department company. Download Bank File is not: the backend
  // keeps it to the caller's own departments.
  const canSeeCompanyLevelActions = hasAllDepartmentCompany(user);
  // Country-only for consistency with the rest of the dashboard widgets.
  // Note: this widget is only mounted from the bureau-only
  // DashboardIreland/DashboardUK (dashboard/index.tsx's `!isBureau` branch
  // routes to DashboardUmbrella instead), so this is presently a no-op for
  // reachability by an umbrella-Ireland tenant.
  const isIreland = payrollCountry === IRELAND;

  const navigate = useNavigateWithQuery();

  const [isOpenCreateEmployeeModal, setIsOpenCreateEmployeeModal] =
    useState(false);
  const [isOpenSubmitFPSModal, setIsOpenSubmitFPSModal] = useState(false);
  const [isOpenSubmitEPSModal, setIsOpenSubmitEPSModal] = useState(false);
  const [isOpenSubmitCIS300Modal, setIsOpenSubmitCIS300Modal] = useState(false);
  const [isOpenSubmitPSRModal, setIsOpenSubmitPSRModal] = useState(false);

  // Each shortcut does the work of one screen, so it is shown only to a user
  // holding the policy of that screen's sidebar item (routeConfig): without it
  // the shortcut would open what the sidebar and the route both refuse. Submit
  // Pension leads nowhere yet, so it has no screen to check.
  const items: {
    label: string;
    icon: JSX.Element;
    onClick: () => void;
    policy?: string;
  }[] = [
    {
      label: 'Add New Employee',
      icon: <Icons.IconUserPlus width={18} height={18} />,
      onClick: () => setIsOpenCreateEmployeeModal(true),
      policy: 'CompanyEmployees',
    },
    ...(canSeeCompanyLevelActions
      ? [
          ...(isIreland
            ? [
                {
                  label: 'Submit PSR',
                  icon: <Icons.IconUpload width={18} height={18} />,
                  onClick: () => setIsOpenSubmitPSRModal(true),
                  policy: 'CompanySubmitPsr',
                },
              ]
            : [
                {
                  label: 'Submit FPS',
                  icon: <Icons.IconUpload width={18} height={18} />,
                  onClick: () => setIsOpenSubmitFPSModal(true),
                  policy: 'CompanyRtiFilings',
                },
                {
                  label: 'Submit EPS',
                  icon: <Icons.IconUpload width={18} height={18} />,
                  onClick: () => setIsOpenSubmitEPSModal(true),
                  policy: 'CompanyRtiFilings',
                },
                {
                  label: 'Submit Pension',
                  icon: <Icons.IconUpload width={18} height={18} />,
                  onClick: () => {},
                },
              ]),
          {
            label: 'Email Payslips',
            icon: <Icons.IconMailDollar width={18} height={18} />,
            onClick: () =>
              navigate('/reports/payroll/payment-summary', {
                query: {
                  from: '/dashboards',
                },
              }),
            policy: 'CompanyReports',
          },
        ]
      : []),
    {
      label: 'Download Bank File',
      icon: <Icons.IconFileDownload width={18} height={18} />,
      onClick: () => navigate('/reports/bank-files'),
      policy: 'CompanyReports',
    },
  ].filter((item) => !item.policy || policies?.includes(item.policy));

  return (
    <div className="EmployerTaxPeriod h-full">
      <AppCard title="I Want To..." className="h-full">
        <div className="flex items-center gap-[16px] flex-wrap p-[16px]">
          {items.map((item) => (
            <div
              key={item.label}
              className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] py-[8px] px-[16px] flex items-center gap-x-[8px] w-fit"
              onClick={item.onClick}
            >
              <div>{item.icon}</div>
              <div className="text-[14px] whitespace-nowrap leading-none">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </AppCard>
      <CreateEmployeeModal
        isOpen={isOpenCreateEmployeeModal}
        toggle={() => setIsOpenCreateEmployeeModal(false)}
      />
      <FPSModal
        isOpen={isOpenSubmitFPSModal}
        toggle={() => setIsOpenSubmitFPSModal(false)}
      />
      <EPSModal
        isOpen={isOpenSubmitEPSModal}
        toggle={() => setIsOpenSubmitEPSModal(false)}
      />
      <CIS300Modal
        isOpen={isOpenSubmitCIS300Modal}
        toggle={() => setIsOpenSubmitCIS300Modal(false)}
      />
      <PSRModal
        isOpen={isOpenSubmitPSRModal}
        toggle={() => setIsOpenSubmitPSRModal(false)}
        type={PSRModalTypeEnum.SUBMIT_PSR}
      />
    </div>
  );
};

export default EmployerIWantTo;
